import { z } from "zod";
import { zStoryInteraction } from "./storyInteraction";
import { zStoryTimerCue } from "./storyTimer";
import { zStoryItemCue } from "./storyItems";
import { zStoryPropCue } from "./storyProps";
import { zStageMark, zStagePosition } from "./stageMovement";
import { zCameraZoom, zCameraZoomCenter, zStoryAspectRatio } from "./storyFraming";
import { zBpm, zChoreo, zChoreoPlay, zGroove } from "./choreo";

const id = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-zA-Z0-9_-]+$/);
const text = z.string().min(1).max(16000);
const level = z.number().finite().min(0).max(1);
const ms = z.number().finite().min(0).max(60000);
const point = z.object({ x: level, y: level }).strict();
const timing = { delayMs: ms.optional(), durationMs: ms.optional(), note: z.string().max(16000).optional() };
const instance = { id, state: z.enum(["on", "off"]).optional() };
const facing = z.enum(["left", "right"]);
/** Stage sounds the player makes itself, on top of any recording: a reveal, a sweep, a sparkle, a big finish. */
export const STAGE_SOUNDS = ["record-scratch", "bass-drop", "whoosh", "sparkle", "cymbal", "boing"] as const;
export const zStageSound = z.enum(STAGE_SOUNDS);
export type StageSound = z.infer<typeof zStageSound>;
const wash = z.enum(["violet-vision", "alarm-red", "memory-sepia", "golden"]);
export const zActionTarget = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("point"), ...point.shape }).strict(),
  z
    .object({
      kind: z.literal("character"),
      character: id,
      offset: z
        .object({ x: z.number().min(-1).max(1), y: z.number().min(-1).max(1) })
        .strict()
        .optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("prop"),
      character: id,
      offset: z
        .object({ x: z.number().min(-1).max(1), y: z.number().min(-1).max(1) })
        .strict()
        .optional(),
    })
    .strict(),
]);
const placement = {
  character: id,
  location: id.optional(),
  mark: zStageMark.optional(),
  nudge: z.number().finite().optional(),
  scale: z.number().positive().optional(),
  layer: z.number().int().min(-9).max(9).optional(),
};
const shape = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("circle"), center: point, radius: z.number().positive().max(1) }).strict(),
  z
    .object({
      kind: z.literal("rounded-rect"),
      x: level,
      y: level,
      width: level,
      height: level,
      cornerRadius: z.number().min(0).max(0.5).optional(),
    })
    .strict(),
]);

/** The sole public action vocabulary. Runtime steps are a compilation detail. */
export const storyActionSchemas = [
  zStoryTimerCue.extend(timing).strict(),
  z
    .object({
      kind: z.literal("say"),
      character: id.optional(),
      speakerLabel: text.optional(),
      text,
      /** The speaker's pose for this line; it holds afterwards, like a pose cue. */
      pose: text.optional(),
      hint: text.optional(),
      offstage: z.enum(["left", "right", "top", "bottom"]).optional(),
      at: point.partial().optional(),
      ...timing,
    })
    .strict(),
  z
    .object({
      kind: z.literal("narrate"),
      text,
      delivery: z.enum(["stage", "director"]).optional(),
      at: point.optional(),
      ...timing,
    })
    .strict(),
  z
    .object({
      kind: z.literal("text"),
      text: text.nullable(),
      style: z.enum(["title", "caption", "projection"]).optional(),
      ...timing,
    })
    .strict(),
  z
    .object({
      kind: z.literal("sound-caption"),
      text,
      tone: z.enum(["whisper", "quiet", "normal", "loud", "explosive"]).optional(),
      target: zActionTarget.optional(),
      description: text.optional(),
      ...timing,
    })
    .strict(),
  z.object({ kind: z.literal("enter"), ...placement, ...timing }).strict(),
  z
    .object({
      kind: z.literal("move"),
      ...placement,
      style: z.enum(["slide", "walk", "pop", "path", "jump", "fly"]).optional(),
      flight: z.enum(["cruise", "takeoff", "land", "hover"]).optional(),
      arcHeight: z.number().min(0).max(3).optional(),
      bank: z.number().min(0).max(60).optional(),
      from: zStagePosition.omit({ slot: true }).optional(),
      via: z
        .array(zStagePosition.omit({ slot: true }))
        .max(24)
        .optional(),
      ...timing,
    })
    .strict(),
  z.object({ kind: z.literal("exit"), character: id, ...timing }).strict(),
  z
    .object({
      kind: z.literal("pose"),
      character: id,
      pose: text.optional(),
      facing: facing.optional(),
      gesture: z.enum(["giggle", "bounce", "nod", "headshake", "wiggle"]).optional(),
      /** A continuous motion that keeps running until changed ("none" stops it). */
      groove: z.union([zGroove, z.literal("none")]).optional(),
      /** The tempo of the groove and of beat-counted choreo steps when no song sets one. */
      bpm: zBpm.optional(),
      /** A pose sequence: one of the character's or the show's by id, written inline, or "none" to stop one. */
      choreo: z.union([id, zChoreo]).optional(),
      /** How the choreo plays: once, in a loop, or a number of times (default: its own `loop`). */
      play: zChoreoPlay.optional(),
      ...timing,
    })
    .strict(),
  z
    .object({
      kind: z.literal("appearance"),
      character: id,
      persona: text.nullable().optional(),
      tint: z.literal("memory-sepia").nullable().optional(),
      silhouette: z.boolean().optional(),
      brightness: level.nullable().optional(),
      ...timing,
    })
    .strict(),
  z
    .object({
      kind: z.literal("backdrop"),
      backdropId: text.optional(),
      variant: text.nullable().optional(),
      surroundColor: text.nullable().optional(),
      ...timing,
    })
    .strict(),
  z
    .object({
      kind: z.literal("camera"),
      /** Exact width/height stage frame, independent of the minimum visible artwork width. */
      aspectRatio: zStoryAspectRatio.nullable().optional(),
      mode: z.enum(["auto", "manual"]).optional(),
      minVisible: z
        .union([z.number().min(1).max(100), z.enum(["phone", "tablet", "square", "landscape", "wide"])])
        .nullable()
        .optional(),
      focus: z
        .union([level, z.object({ character: id }).strict(), z.object({ characters: z.array(id).min(1) }).strict()])
        .nullable()
        .optional(),
      /** Magnifies the backdrop and everything standing in it over `durationMs`; 0 ms cuts. */
      zoom: zCameraZoom.nullable().optional(),
      zoomCenter: zCameraZoomCenter.nullable().optional(),
      ...timing,
    })
    .strict(),
  z.object({ kind: z.literal("curtain"), state: z.enum(["open", "closed"]), ...timing }).strict(),
  z
    .object({
      kind: z.literal("layout"),
      mode: z.enum(["fullscreen", "stage", "compact", "standard", "performersLarge", "performersOnly"]).nullable(),
      ...timing,
    })
    .strict(),
  z
    .object({
      kind: z.literal("light"),
      target: z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("stage") }).strict(),
        z.object({ kind: z.literal("location"), location: id }).strict(),
      ]),
      level: level.nullable(),
      ...timing,
    })
    .strict(),
  z
    .object({
      kind: z.literal("spotlight"),
      ...instance,
      target: zActionTarget.optional(),
      brightness: level.optional(),
      color: text.optional(),
      radius: z.number().positive().max(1).optional(),
      ...timing,
    })
    .strict(),
  z.object({ kind: z.literal("wash"), preset: wash.nullable(), pulse: z.boolean().optional(), ...timing }).strict(),
  z.object({ kind: z.literal("blackout"), keepSpotlights: z.boolean().optional(), ...timing }).strict(),
  z.object({ kind: z.literal("restore-lights"), ...timing }).strict(),
  z.object({ kind: z.literal("light-effect"), preset: z.enum(["flash", "flicker", "sweep"]), ...timing }).strict(),
  z
    .object({
      kind: z.literal("glow"),
      ...instance,
      target: zActionTarget.optional(),
      style: z.enum(["steady", "breathing", "rings"]).optional(),
      color: text.optional(),
      size: z.number().positive().optional(),
      ...timing,
    })
    .strict(),
  z
    .object({
      kind: z.literal("beam"),
      ...instance,
      from: zActionTarget.optional(),
      to: zActionTarget.optional(),
      color: text.optional(),
      ...timing,
    })
    .strict(),
  z
    .object({
      kind: z.literal("particles"),
      preset: z.enum(["bubbles", "hearts", "glitter", "smoke", "clouds"]),
      target: zActionTarget.optional(),
      from: zActionTarget.optional(),
      to: zActionTarget.optional(),
      via: z.array(zActionTarget).max(24).optional(),
      follow: id.optional(),
      emissionRate: z.number().min(1).max(60).optional(),
      fadeMs: z.number().min(50).max(10000).optional(),
      scale: z.number().min(0.1).max(10).optional(),
      offset: z
        .object({ x: z.number().min(-1).max(1), y: z.number().min(-1).max(1) })
        .strict()
        .optional(),
      anchor: z.enum(["body", "feet"]).optional(),
      ...timing,
    })
    .strict(),
  z
    .object({ kind: z.literal("highlight"), ...instance, shape: shape.optional(), color: text.optional(), ...timing })
    .strict(),
  z.object({ kind: z.literal("shake"), intensity: z.number().min(0).max(3).optional(), ...timing }).strict(),
  z.object({ kind: z.literal("sound"), sound: zStageSound, volume: z.number().min(0).max(1).optional(), ...timing }).strict(),
  zStoryItemCue.extend(timing).strict(),
  zStoryPropCue.extend(timing).strict(),
] as const;

export const zStoryAction = z.discriminatedUnion("kind", [...storyActionSchemas]);
export type StoryAction = z.infer<typeof zStoryAction>;
export type StoryActionKind = StoryAction["kind"];
export const ACTION_GROUPS = [
  { id: "words", label: "Words", icon: "MessageSquare" },
  { id: "characters", label: "Characters", icon: "Users" },
  { id: "stage", label: "Stage", icon: "PanelsTopLeft" },
  { id: "lighting", label: "Lighting", icon: "Lamp" },
  { id: "effects", label: "Visual effects", icon: "Sparkles" },
  { id: "objects", label: "Objects", icon: "Package" },
] as const;
type Group = (typeof ACTION_GROUPS)[number]["id"];
export interface ActionDefinition {
  kind: StoryActionKind;
  label: string;
  group: Group;
  icon: string;
  setup: boolean;
  setupLabel?: string;
  description: string;
  aliases?: string;
}
export const ACTION_REGISTRY: readonly ActionDefinition[] = [
  {
    kind: "timer",
    label: "Activity timer",
    group: "words",
    icon: "Timer",
    setup: false,
    description: "Start or clear a small real-time countdown in the top right",
    aliases: "countdown seconds brushing",
  },
  {
    kind: "say",
    label: "Say",
    group: "words",
    icon: "MessageCircle",
    setup: false,
    description: "A character's spoken words",
    aliases: "line dialogue speech",
  },
  {
    kind: "narrate",
    label: "Narrate",
    group: "words",
    icon: "BookOpen",
    setup: false,
    description: "The storyteller's voice",
    aliases: "narrator",
  },
  {
    kind: "text",
    label: "Show text",
    group: "words",
    icon: "Type",
    setup: true,
    description: "A title, caption or inscription",
    aliases: "title heading projection",
  },
  {
    kind: "sound-caption",
    label: "Sound caption",
    group: "words",
    icon: "BadgeAlert",
    setup: false,
    description: "A comic sound burst",
    aliases: "sfx sound",
  },
  {
    kind: "enter",
    label: "Enter",
    setupLabel: "Place character",
    group: "characters",
    icon: "LogIn",
    setup: true,
    description: "Place a character onstage",
    aliases: "cast opening",
  },
  {
    kind: "move",
    label: "Move",
    group: "characters",
    icon: "MoveRight",
    setup: false,
    description: "Travel to a new mark",
    aliases: "slide jump fly path",
  },
  {
    kind: "exit",
    label: "Exit",
    setupLabel: "Remove character",
    group: "characters",
    icon: "LogOut",
    setup: true,
    description: "Take a character offstage",
  },
  {
    kind: "pose",
    label: "Pose",
    setupLabel: "Pose & facing",
    group: "characters",
    icon: "PersonStanding",
    setup: true,
    description:
      "Set pose and facing, onstage or offstage; add a gesture, a continuous groove (bop, sway, step, shimmy, hop, groove) or a choreo (a timed sequence of poses, once or looping, synced to the beat)",
    aliases: "reaction orientation gesture groove dance choreo sequence",
  },
  {
    kind: "appearance",
    label: "Appearance",
    group: "characters",
    icon: "Palette",
    setup: true,
    description: "Persona, tint, silhouette and brightness",
    aliases: "roles costume",
  },
  {
    kind: "backdrop",
    label: "Backdrop",
    group: "stage",
    icon: "Image",
    setup: true,
    description: "Set, picture variant and surround color",
    aliases: "backdropVariant ground scenery",
  },
  { kind: "camera", label: "Camera", group: "stage", icon: "Camera", setup: true, description: "Focus and framing" },
  {
    kind: "curtain",
    label: "Curtain",
    group: "stage",
    icon: "PanelTopClose",
    setup: true,
    description: "An intentional reveal",
  },
  {
    kind: "layout",
    label: "Layout",
    group: "stage",
    icon: "PanelsTopLeft",
    setup: true,
    description: "Stage composition",
  },
  {
    kind: "light",
    label: "Light level",
    group: "lighting",
    icon: "Sun",
    setup: true,
    description: "Stage or location brightness",
    aliases: "house dim brighten",
  },
  {
    kind: "spotlight",
    label: "Spotlight",
    group: "lighting",
    icon: "Flashlight",
    setup: true,
    description: "Illuminate a character or artwork area",
    aliases: "followspots follow spot",
  },
  {
    kind: "wash",
    label: "Color wash",
    group: "lighting",
    icon: "PaintBucket",
    setup: true,
    description: "The scene's color mood",
  },
  {
    kind: "blackout",
    label: "Blackout",
    group: "lighting",
    icon: "Moon",
    setup: true,
    description: "Turn stage lighting off",
  },
  {
    kind: "restore-lights",
    label: "Restore lights",
    group: "lighting",
    icon: "RotateCcw",
    setup: false,
    description: "Restore this scene's opening lighting",
  },
  {
    kind: "light-effect",
    label: "Light effect",
    group: "lighting",
    icon: "SunDim",
    setup: false,
    description: "Flash, flicker or sweep",
  },
  {
    kind: "glow",
    label: "Glow",
    group: "effects",
    icon: "Orbit",
    setup: true,
    description: "A halo around a target",
    aliases: "point-glow pulse",
  },
  { kind: "beam", label: "Beam", group: "effects", icon: "Route", setup: true, description: "A visible connection" },
  {
    kind: "particles",
    label: "Particles",
    group: "effects",
    icon: "Sparkles",
    setup: false,
    description: "Glitter, hearts, bubbles, smoke and clouds",
    aliases: "sparkle",
  },
  {
    kind: "highlight",
    label: "Highlight",
    group: "effects",
    icon: "Scan",
    setup: true,
    description: "Outline a detail",
    aliases: "emphasis",
  },
  {
    kind: "shake",
    label: "Shake",
    group: "effects",
    icon: "Vibrate",
    setup: false,
    description: "A jolt of stage motion",
  },
  {
    kind: "sound",
    label: "Sound effect",
    group: "effects",
    icon: "Volume2",
    setup: false,
    description: "A short stage sound: a record scratch, a bass drop, a whoosh, a sparkle, a cymbal or a boing",
    aliases: "sfx noise",
  },
  {
    kind: "item",
    label: "Item event",
    group: "objects",
    icon: "Package",
    setup: false,
    description: "Ownership, use and discovery",
  },
  {
    kind: "prop",
    label: "Prop",
    group: "objects",
    icon: "Package",
    setup: true,
    description: "Show an Item, switch its named picture variation, or move it along a path",
    aliases: "foreground object panel path",
  },
];

export function validateAction(action: StoryAction, context: z.RefinementCtx, path: (string | number)[] = []) {
  const fail = (message: string) => context.addIssue({ code: "custom", path, message });
  if (["spotlight", "glow"].includes(action.kind) && (action as any).state !== "off" && !(action as any).target)
    fail("Choose a target for this effect.");
  if (action.kind === "beam" && action.state !== "off" && (!action.from || !action.to))
    fail("A beam needs both endpoints.");
  if (action.kind === "highlight" && action.state !== "off" && !action.shape) fail("Choose a highlight shape.");
  if (action.kind === "spotlight" && action.target?.kind === "character" && action.radius !== undefined)
    fail("Character spotlights follow their character and do not have a radius.");
  if (
    action.kind === "backdrop" &&
    action.backdropId === undefined &&
    action.variant === undefined &&
    action.surroundColor === undefined
  )
    fail("Choose a backdrop, variant or surround color.");
  if (action.kind === "pose" && !action.pose && !action.facing && !action.gesture && !action.groove && !action.choreo)
    fail("Choose a pose, facing, gesture, groove or choreo.");
  if (action.kind === "pose" && action.play !== undefined && !action.choreo) fail("Choose the choreo to play.");
  if (action.kind === "item") {
    if (action.action !== "use" && action.abilityId) fail("An ability can only be selected for a Use item cue.");
    if (action.action === "use" && !action.abilityId) fail("Choose the item's ability to use.");
    if (action.action === "give" && (!action.toCharacter || action.toCharacter === action.character))
      fail("Choose another character to receive the item.");
  }
}
export const zActionCue = z
  .object({
    id,
    label: text.optional(),
    note: z.string().max(16000).optional(),
    /** Match a visual beat to these sung words, without making a character speak or sing. */
    lyricCue: z.string().min(1).max(500).optional(),
    actions: z.array(zStoryAction).max(100),
    holdMs: ms.optional(),
    interaction: zStoryInteraction.optional(),
  })
  .strict()
  .superRefine((cue, ctx) => {
    if (!cue.actions.length && !cue.holdMs)
      ctx.addIssue({ code: "custom", message: "An empty beat needs a hold duration." });
    if (cue.actions.filter((a) => a.kind === "say" || a.kind === "narrate").length > 1)
      ctx.addIssue({ code: "custom", message: "Keep one spoken turn per beat." });
    cue.actions.forEach((a, i) => validateAction(a, ctx, ["actions", i]));
  });
export const zSceneSetup = z
  .object({ actions: z.array(zStoryAction).max(200) })
  .strict()
  .superRefine((setup, ctx) => {
    setup.actions.forEach((a, i) => {
      validateAction(a, ctx, ["actions", i]);
      if (
        !ACTION_REGISTRY.find((d) => d.kind === a.kind)?.setup ||
        a.delayMs !== undefined ||
        a.durationMs !== undefined ||
        (a.kind === "pose" && a.gesture) ||
        // A loop (or a groove) may already be running when the scene opens; a one-off belongs in a beat.
        (a.kind === "pose" &&
          a.choreo !== undefined &&
          a.choreo !== "none" &&
          (a.play === "once" || typeof a.play === "number" || (a.play === undefined && typeof a.choreo === "object" && !a.choreo.loop))) ||
        (a.kind === "text" && a.style !== "projection")
      )
        ctx.addIssue({
          code: "custom",
          path: ["actions", i],
          message:
            "Setup establishes persistent state before playback. Speech, titles, captions, timed cues and gestures belong in a beat; setup text uses projection.",
        });
    });
  });
export type ActionCue = z.infer<typeof zActionCue>;
export type SceneSetup = z.infer<typeof zSceneSetup>;

export function defaultStoryAction(kind: StoryActionKind, character: string, asset: string): StoryAction {
  const at = { kind: "point" as const, x: 0.5, y: 0.5 };
  const instanceId = `${kind}-${Math.random().toString(36).slice(2, 10)}`;
  switch (kind) {
    case "prop":
      return { kind, itemId: "item", state: "show", at: { x: 0.5, y: 0.5 }, width: 0.3, layer: "foreground" };
    case "say":
      return { kind, character, text: "A new line." };
    case "narrate":
      return { kind, text: "A new moment." };
    case "text":
      return { kind, text: "A new chapter", style: "caption" };
    case "timer":
      return { kind, seconds: 120, label: "Brushing" };
    case "sound-caption":
      return { kind, text: "THUMP!", tone: "loud" };
    case "enter":
      return { kind, character, location: "stage-bottom", mark: 3 };
    case "move":
      return { kind, character, mark: 3, style: "slide", durationMs: 900 };
    case "exit":
      return { kind, character };
    case "pose":
      return { kind, character, facing: "right" };
    case "appearance":
      return { kind, character, persona: null };
    case "backdrop":
      return { kind, backdropId: asset };
    case "camera":
      return { kind, mode: "auto" };
    case "curtain":
      return { kind, state: "open" };
    case "layout":
      return { kind, mode: null };
    case "light":
      return { kind, target: { kind: "stage" }, level: 0.5 };
    case "spotlight":
      return { kind, id: instanceId, target: { kind: "character", character }, brightness: 1 };
    case "wash":
      return { kind, preset: "golden" };
    case "blackout":
      return { kind };
    case "restore-lights":
      return { kind };
    case "light-effect":
      return { kind, preset: "flicker", durationMs: 1200 };
    case "glow":
      return { kind, id: instanceId, target: at, style: "steady" };
    case "beam":
      return { kind, id: instanceId, from: at, to: { ...at, x: 0.8 } };
    case "particles":
      return { kind, preset: "glitter", follow: character, durationMs: 700 };
    case "highlight":
      return { kind, id: instanceId, shape: { kind: "circle", center: { x: 0.5, y: 0.5 }, radius: 0.1 } };
    case "shake":
      return { kind, intensity: 1 };
    case "sound":
      return { kind, sound: "whoosh" };
    case "item":
      return {
        kind,
        action: "discover",
        itemId: instanceId,
        character,
        item: {
          id: instanceId,
          name: "New item",
          description: "An object in the story.",
          icon: "📦",
          abilities: [],
          limitations: [],
        },
      };
  }
}
